import React, { FC, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import DryVanIcon from 'transfix-icons/build/DryVanIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import EmailIcon from 'transfix-icons/build/EmailIcon'
import PhoneIcon from 'transfix-icons/build/PhoneIcon'
import SmartphoneOffIcon from 'transfix-icons/build/SmartphoneOffIcon'
import SmartphoneOnIcon from 'transfix-icons/build/SmartphoneOnIcon'
import UserIcon from 'transfix-icons/build/UserIcon'
import { Box, Divider, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { ResponsiveLayoutRow } from 'apps/fleet-planner/components/layout/ResponsiveLayoutRow'
import { TeamMemberCarrierDisplay } from 'apps/fleet-planner/components/team/TeamMemberCarrierDisplay'
import { TeamMemberInfoRow } from 'apps/fleet-planner/components/team/TeamMemberInfoRow'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { ITeamMember } from 'apps/fleet-planner/types'
import { Avatar } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import formatPhone from 'utils/formatPhone'
import { hasDrivers } from 'utils/teamMemberHelpers'

const useTeamMemberInfoStyles = makeStyles({
  divider: {
    borderColor: brandColors.coolGray1,
  },
})

interface ITeamMemberInfoProps {
  /**
   * A function to be executed when the back button is clicked
   */
  onPressBackButton?: () => void
  /**
   * A function to be executed when the edit button is clicked
   */
  onEditTeamMember?: (teamMember?: ITeamMember) => void
}

export const TeamMemberInfoPage: FC<ITeamMemberInfoProps> = ({
  onEditTeamMember,
  onPressBackButton,
}) => {
  const history = useHistory()

  const {
    selectedTeamMember: teamMember,
  }: { selectedTeamMember?: ITeamMember } = useTeamMemberContext()

  if (!teamMember?.id) {
    history.replace('/team')
  }

  const { cellPhone, phone, motorCarrierDetails = [], smsStatusUpdatesEnabled } = teamMember || {}
  const phoneFormatted = phone ? formatPhone(phone, { removeCountryCode: '+1' }) : '---'
  const cellPhoneFormatted = cellPhone ? formatPhone(cellPhone, { removeCountryCode: '+1' }) : '---'
  const roles = useMemo(() => {
    const rolesList = [...motorCarrierDetails, ...motorCarrierDetails].map(mcd =>
      capitalize((mcd.userType || '').replace('_', ' '))
    )
    return [...new Set(rolesList)].sort()
  }, [motorCarrierDetails])
  const fmsTeamMembersShowDriverSmsOptInStatusEnabled = useFeatureFlag(
    'fmsTeamMembersShowDriverSmsOptInStatus'
  )
  const isDriver = useMemo(
    () => fmsTeamMembersShowDriverSmsOptInStatusEnabled && hasDrivers(motorCarrierDetails),
    [fmsTeamMembersShowDriverSmsOptInStatusEnabled, motorCarrierDetails]
  )

  const smartPhoneIcon = smsStatusUpdatesEnabled ? SmartphoneOnIcon : SmartphoneOffIcon

  const classes = useTeamMemberInfoStyles()

  const pressBackButtonHandler = () => {
    if (onPressBackButton) {
      onPressBackButton()
    }
  }

  const createPrimaryActionClickHandler = (teamMember?: ITeamMember) => () => {
    if (onEditTeamMember && teamMember) {
      onEditTeamMember(teamMember)
    }
  }

  return (
    <ResponsiveLayout
      title='Team Member'
      onPressBackButton={pressBackButtonHandler}
      primaryActionProps={{
        label: 'Edit Team Member',
        fabIcon: EditIcon,
        fabColor: 'secondary',
        onClick: createPrimaryActionClickHandler(teamMember),
      }}>
      <ResponsiveLayoutRow BoxProps={{ pt: 0.5 }}>
        <Box display='flex' alignItems='center' pb={2.5}>
          <Box mr={2}>
            <Avatar name={`${teamMember?.firstName} ${teamMember?.lastName}`} size='medium' />
          </Box>
          <Box>
            <Typography variant='h3'>
              {teamMember?.firstName} {teamMember?.lastName}
            </Typography>
          </Box>
        </Box>
      </ResponsiveLayoutRow>

      <Divider className={classes.divider} />

      <ResponsiveLayoutRow>
        <TeamMemberInfoRow icon={EmailIcon} title='Contact email'>
          <Typography className='fs-mask' variant='body2'>
            {teamMember?.email}
          </Typography>
        </TeamMemberInfoRow>

        <TeamMemberInfoRow icon={PhoneIcon} title='Phone Number(s)'>
          <Typography className='fs-mask' variant='body2'>
            P: {phoneFormatted}
          </Typography>
          <Typography className='fs-mask' variant='body2'>
            C: {cellPhoneFormatted}
          </Typography>
        </TeamMemberInfoRow>

        {isDriver && (
          <TeamMemberInfoRow icon={smartPhoneIcon} title='SMS OPT-IN'>
            <Typography className='fs-mask' variant='body2'>
              Indicates if driver is opted into SMS Messaging for non-Transifx loads.
            </Typography>
          </TeamMemberInfoRow>
        )}

        <TeamMemberInfoRow icon={UserIcon} title='Role(s)'>
          {roles.map(role => (
            <Typography key={role} variant='body2'>
              {role}
            </Typography>
          ))}
        </TeamMemberInfoRow>

        <TeamMemberInfoRow icon={DryVanIcon} title='Carrier(s)'>
          <Typography variant='body2'>
            <TeamMemberCarrierDisplay motorCarriers={motorCarrierDetails} />
          </Typography>
        </TeamMemberInfoRow>
      </ResponsiveLayoutRow>
    </ResponsiveLayout>
  )
}
